import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Check, ArrowRight, Minus, Plus, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Navbar } from "../components/Navbar";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Billing() {
  const { t } = useTranslation();
  const [selectedPlan, setSelectedPlan] = useState('pack_50');
  const [carteQuantity, setCarteQuantity] = useState(5);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState(null);
  
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchUser();
    fetchPricing();
    checkPaymentStatus();
  }, []);

  const fetchUser = async () => {
    try {
      const res = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
      if (res.ok) setUser(await res.json());
    } catch (e) {
      console.error(e);
    }
  };

  const fetchPricing = async () => {
    try {
      const res = await fetch(`${API}/api/payments/packages`, { credentials: 'include' });
      if (res.ok) {
        const packages = await res.json();
        const pricingMap = {};
        for (const pkg of packages) {
          pricingMap[pkg.package_id] = pkg;
        }
        setPricing(pricingMap);
      }
    } catch (e) {
      console.error('Error fetching pricing:', e);
    }
  };

  const checkPaymentStatus = async () => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      const toastId = toast.loading(t('common.loading'));
      try {
        const res = await fetch(`${API}/api/payments/status/${sessionId}`, { credentials: 'include' });
        const data = await res.json();
        
        if (data.status === 'completed') {
          toast.success(`${t('success.saved')} ${data.credits_added} credits`, { id: toastId });
          fetchUser(); // Refresh credits
          navigate('/billing', { replace: true });
        } else {
          toast.info("Processing...", { id: toastId });
        }
      } catch (e) {
        toast.error(t('errors.generic'), { id: toastId });
      }
    }
  };

  const handlePayment = async (planId = null) => {
    setLoading(true);
    try {
      const activePlan = planId || selectedPlan;
      let quantity = 1;
      if (activePlan === 'carte') {
        quantity = carteQuantity;
      }

      const res = await fetch(`${API}/api/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package_id: activePlan,
          quantity: quantity,
          origin_url: window.location.origin
        }),
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Checkout creation failed');
      
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(t('errors.network'));
      console.error(e);
      setLoading(false);
    }
  };

  const cartePrice = pricing?.carte?.price || 15;
  const pack50Price = pricing?.pack_50?.price || 500;
  const pack50Credits = pricing?.pack_50?.credits || 50;
  const pack120Price = pricing?.pack_120?.price || 1000;
  const pack120Credits = pricing?.pack_120?.credits || 120;

  const plans = [
    {
      id: 'carte',
      name: t('billing.packages.perInterview'),
      priceDisplay: `$${cartePrice}`,
      unit: `$${cartePrice} / ` + t('nav.interviews'),
      features: [
        `$${cartePrice} ${t('billing.packages.perInterviewDesc')}`,
        "No commitment",
        "All features",
        "Standard support"
      ],
      highlight: false
    },
    {
      id: 'pack_50',
      name: pricing?.pack_50?.name || t('billing.packages.pack50'),
      priceDisplay: `$${pack50Price}`,
      unit: `${pack50Credits} AI interviews`,
      save: t('billing.packages.savings') + ` $${(cartePrice * pack50Credits - pack50Price)}`,
      features: [
        `$${(pack50Price / pack50Credits).toFixed(2)} / interview`,
        "12 months validity",
        "Priority support",
        "Advanced analytics"
      ],
      highlight: true
    },
    {
      id: 'pack_120',
      name: pricing?.pack_120?.name || t('billing.packages.pack120'),
      priceDisplay: `$${pack120Price}`,
      unit: `${pack120Credits} AI interviews`,
      save: t('billing.packages.savings') + ` $${(cartePrice * pack120Credits - pack120Price)}`,
      features: [
        `$${(pack120Price / pack120Credits).toFixed(2)} / interview`,
        "Unlimited validity",
        "Dedicated account manager",
        "Unlimited API access"
      ],
      highlight: false
    }
  ];

  return (
    <div className="min-h-screen bg-canvas dark:bg-zinc-950">
      <Navbar user={user} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <div className="text-center mb-10 sm:mb-16">
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-ink mb-4">
            {t('billing.title')}
          </h1>
          <p className="text-xl text-ink-muted max-w-2xl mx-auto">
            {t('billing.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <div 
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              className={`relative rounded-2xl transition-all duration-300 cursor-pointer ${
                selectedPlan === plan.id 
                  ? "transform -translate-y-2 shadow-2xl ring-2 ring-brand-primary" 
                  : "hover:-translate-y-1 hover:shadow-xl border border-zinc-200 dark:border-zinc-700"
              }`}
            >
              {plan.highlight && (
                <div className="absolute -top-4 left-0 right-0 flex justify-center">
                  <Badge className="bg-brand-primary text-white px-4 py-1 text-sm shadow-lg">
                    {t('reports.recommended')}
                  </Badge>
                </div>
              )}
              
              <Card className={`h-full border-0 ${selectedPlan === plan.id ? 'bg-zinc-50 dark:bg-zinc-800' : 'bg-white dark:bg-zinc-900'}`}>
                <CardContent className="p-8 flex flex-col h-full">
                  <div className="mb-6">
                    <h3 className="font-display font-bold text-xl text-ink mb-2">{plan.name}</h3>
                    
                    {plan.id === 'carte' ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-display font-bold text-4xl text-ink">${cartePrice * carteQuantity}</span>
                          <span className="text-ink-muted text-sm">/ {carteQuantity}</span>
                        </div>
                        
                        <div className="flex items-center gap-3 bg-white dark:bg-zinc-800 p-2 rounded-lg border border-zinc-200 dark:border-zinc-700" onClick={(e) => e.stopPropagation()}>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-8 w-8 rounded-full"
                            onClick={() => setCarteQuantity(Math.max(1, carteQuantity - 1))}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <Input 
                            type="number" 
                            value={carteQuantity}
                            onChange={(e) => setCarteQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                            className="h-8 w-16 text-center border-0 p-0 focus-visible:ring-0"
                          />
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-8 w-8 rounded-full"
                            onClick={() => setCarteQuantity(carteQuantity + 1)}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span className="font-display font-bold text-4xl text-ink">{plan.priceDisplay}</span>
                        <span className="text-ink-muted text-sm">{plan.unit}</span>
                      </div>
                    )}
                    
                    {plan.save && (
                      <span className="inline-block mt-2 text-xs font-semibold text-green-600 bg-green-100 px-2 py-1 rounded">
                        {plan.save}
                      </span>
                    )}
                  </div>

                  <ul className="space-y-4 mb-8 flex-1">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-ink-muted">
                        <Check className={`w-5 h-5 flex-shrink-0 ${
                          selectedPlan === plan.id ? "text-brand-primary" : "text-zinc-400"
                        }`} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePayment(plan.id);
                    }}
                    className={`w-full h-12 font-medium transition-all ${
                      selectedPlan === plan.id 
                        ? "bg-brand-primary hover:bg-brand-primary-dark text-white dark:text-zinc-900 shadow-lg" 
                        : "bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 text-ink hover:border-brand-primary hover:text-brand-primary"
                    }`}
                    disabled={loading}
                  >
                    {loading && selectedPlan === plan.id ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <>
                        {t('billing.checkout')} {plan.id === 'carte' ? `$${cartePrice * carteQuantity}` : plan.priceDisplay} <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center max-w-4xl mx-auto">
          <p className="text-sm text-ink-muted">
            <a href="mailto:contact@emaai.tech" className="text-brand-primary hover:underline">contact@emaai.tech</a>
          </p>
        </div>
      </main>
    </div>
  );
}
